/** @jsxImportSource @emotion/react */

import classNames from 'classnames';
import { switchUserTournament } from 'modules/auth/store/actions';
import AuthLayout from 'modules/auth/ui/layouts';
import { useAuthGuard } from 'modules/auth/ui/hooks/use-auth-guard';
import { useAvailableTournaments } from 'modules/auth/ui/hooks/use-available-tournaments';
import { Pages } from 'modules/navigation/domain/enums/pages';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { ClipLoader } from 'react-spinners';
import { toast } from 'react-toastify';
import { scrollbar } from 'shared/styles/scrollbar';

import { AppDispatch } from 'store/store';
import { theme } from 'styles/theme/theme';
import { UDText } from 'ud-ui/components';
import UDButton from 'ud-ui/components/button';
import Pagination from 'ud-ui/components/pagination';
import UDSearchInput from 'ud-ui/components/search';

const AuthPagesSelectTournament = () => {
  useAuthGuard();

  const dispatch = useDispatch<AppDispatch>();
  const { isLoading, isError, data: availableTournaments, pages, page, search, setSearch, setPage } = useAvailableTournaments();

  const handleTournamentSelected = useCallback(
    (tournamentId: number) => async () => {
      dispatch(switchUserTournament(tournamentId));
    },
    [dispatch],
  );

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (availableTournaments.length > 0) {
          void handleTournamentSelected(availableTournaments[0].id)();
        }
      }
    },
    [availableTournaments, handleTournamentSelected],
  );

  useEffect(() => {
    if (isError) {
      toast.error('Произошла ошибка при получении турниров, перезагрузите страницу');
    }
  }, [isError]);

  const body = useMemo(() => {
    if (isError) {
      return (
        <UDText variant="text-2" className="text-WhiteGray mt-7.5 text-center">
          Произошла ошибка, попробуйте ещё раз
        </UDText>
      );
    }

    // Спиннер только пока нечего показать: isLoading — это isFetching, и на каждом
    // дебаунс-рефетче он схлопывал бы уже отрисованный список
    if (isLoading && !availableTournaments.length) {
      return (
        <div className="flex-center h-[320px] w-full">
          <ClipLoader color="white" />
        </div>
      );
    }

    if (!availableTournaments.length) {
      return (
        <UDText variant="text-2" className="text-WhiteGray mt-7.5 text-center">
          Организатора с таким названием нет.
        </UDText>
      );
    }

    return (
      <div css={scrollbar(theme)} className="min-h-0 flex-1 overflow-y-auto">
        {availableTournaments.map((tournament, index) => (
          <div className={classNames({ 'mt-2.5': index !== 0 })} key={tournament.id}>
            <UDButton className="w-full" variant="primary" type="button" onClick={handleTournamentSelected(tournament.id)}>
              {tournament.name}
            </UDButton>
          </div>
        ))}
        <div className="flex-center w-full">
          <Pagination pages={pages} currentPage={page} onPageChange={setPage} pagesShow={8} className="mt-7.5" />
        </div>
      </div>
    );
  }, [availableTournaments, handleTournamentSelected, isError, isLoading, page, pages, setPage]);

  return (
    <AuthLayout
      title=""
      subtitle=""
      logoTo={Pages.INDEX}
      contentClassName="overflow-hidden min-h-0"
      formContainerProps={{
        className: 'flex flex-col h-full min-h-0',
        style: {
          height: 522,
          justifyContent: 'flex-start',
        },
      }}
    >
      <div className="flex h-full min-h-0 flex-col">
        <UDText as="h2" variant="title-2" color="white" className="mb-5">
          Список организаторов
        </UDText>
        <UDSearchInput
          autoFocus
          placeholder="Название организации"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={handleSearchKeyDown}
          className="mb-7.5"
        />
        {body}
      </div>
    </AuthLayout>
  );
};

export default AuthPagesSelectTournament;
